import { getMediaQuerySelector } from '../utilities/media-queries'

export type ColorScheme = 'light' | 'dark'
export type ColorSchemePreference = ColorScheme | 'system'

const storageKey = 'color-scheme'

const darkModeQuery = matchMedia(getMediaQuerySelector({ prefersColorScheme: 'dark' }))

const getSystemColorScheme = (): ColorScheme => (darkModeQuery.matches ? 'dark' : 'light')

const getStoredPreference = (): ColorSchemePreference | null => {
  const stored = localStorage.getItem(storageKey)
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  return null
}

const resolveColorScheme = (preference: ColorSchemePreference): ColorScheme => {
  if (preference === 'system') return getSystemColorScheme()
  return preference
}

const applyColorScheme = (colorScheme: ColorScheme) => {
  document.documentElement.dataset['colorScheme'] = colorScheme
}

/** Default when the user has never chosen: dark (brand default). */
export const getDefaultPreference = (): ColorSchemePreference => 'dark'

export const initColorScheme = () => {
  const preference = getStoredPreference() ?? getDefaultPreference()
  applyColorScheme(resolveColorScheme(preference))

  darkModeQuery.addEventListener('change', () => {
    const current = getStoredPreference() ?? getDefaultPreference()
    if (current === 'system') applyColorScheme(getSystemColorScheme())
  })
}

export const setColorSchemePreference = (preference: ColorSchemePreference) => {
  localStorage.setItem(storageKey, preference)
  applyColorScheme(resolveColorScheme(preference))
}

/** @deprecated Prefer setColorSchemePreference — kept for call sites that force light/dark */
export const setColorScheme = (colorScheme: ColorScheme) => {
  setColorSchemePreference(colorScheme)
}

export const clearColorSchemeOverride = () => {
  localStorage.removeItem(storageKey)
  applyColorScheme(resolveColorScheme(getDefaultPreference()))
}

export const getColorSchemePreference = (): ColorSchemePreference => {
  return getStoredPreference() ?? getDefaultPreference()
}

export const getColorScheme = (): ColorScheme => {
  return resolveColorScheme(getColorSchemePreference())
}
